import { useEffect, useMemo, useState } from 'react';
import { Buffer } from 'buffer';

import { useError } from 'components/ErrorBoundary/ErrorBoundary';

const BASE_64_REGEX =
  /^([0-9a-zA-Z+/]{4})*(([0-9a-zA-Z+/]{2}==)|([0-9a-zA-Z+/]{3}=))?$/;

const VALID_URL_REGEX =
  /^(http[s]?:\/\/(www\.)?|ftp:\/\/(www\.)?|www\.){1}([0-9A-Za-z-.@:%_+~#=]+)+((\.[a-zA-Z]{2,3})+)(\/(.)*)?(\?(.)*)?/;

const INVALID_CODE_MESSAGE = 'Invalid code provided.';

export default function useDecodedPromoCode(hash: string) {
  const { triggerError } = useError();
  const [isDecoding, setIsDecoding] = useState(true);
  const [partner, setPartner] = useState<string>('');
  const [locale, setLocale] = useState<string>('');
  const [redirectUrl, setRedirectUrl] = useState<string>('');
  const [redeemCode, setRedeemCode] = useState<string>('');
  const isBase64 = useMemo(() => BASE_64_REGEX.test(hash), [hash]);
  const isValidUrl = (url: string) => VALID_URL_REGEX.test(url);

  useEffect(() => {
    try {
      if (!isBase64) throw new Error(INVALID_CODE_MESSAGE);
      const [
        decodedLocale,
        decodedPartner,
        decodedRedirectUrl,
        decodedRedeemCode,
      ] = Buffer.from(hash, 'base64').toString('ascii').split(',');

      if (!isValidUrl(decodedRedirectUrl))
        throw new Error(INVALID_CODE_MESSAGE);

      setPartner(decodedPartner);
      setLocale(decodedLocale);
      setRedirectUrl(decodedRedirectUrl);
      setRedeemCode(decodedRedeemCode);
    } catch (err) {
      if (err instanceof Error) triggerError(err);
    } finally {
      setIsDecoding(false);
    }
  }, [hash, isBase64, triggerError]);

  return {
    isDecoding,
    partner,
    locale,
    redirectUrl,
    redeemCode,
  };
}
